"use client";

import { useState, useEffect, useRef, useCallback, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { isSupabaseConfigured } from "@/lib/supabase/config";
import { safeReturnTo } from "@/lib/auth/safe-return-to";
import { BrandLogoMark, BrandSpinner } from "@/components/brand";

export default function SignInPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-brand-paper">
          <BrandSpinner size={28} />
        </div>
      }
    >
      <SignInPageContent />
    </Suspense>
  );
}

function SignInPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const useSupabase = isSupabaseConfigured();

  // Validated: an unchecked returnTo would let a crafted sign-in link bounce
  // the user off-origin from a trusted vaon.app page. See safeReturnTo.
  const returnTo = safeReturnTo(
    searchParams.get("returnTo"),
    typeof window !== "undefined" ? window.location.href : "https://vaon.app/sign-in"
  );
  const finishSignIn = useCallback(() => {
    if (returnTo.split(/[?#]/, 1)[0] === "/ipad-transfer") {
      // The native file grant checks the main frame's document request URL.
      // Start a new document so a sign-in page cannot remain that request.
      window.location.assign(returnTo);
      return;
    }
    router.push(returnTo);
    router.refresh();
  }, [returnTo, router]);

  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode]         = useState<"password" | "magic_link" | "forgot_password">("password");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);
  const [autoLoggingIn, setAutoLoggingIn] = useState(false);
  const [recoveryProcessing, setRecoveryProcessing] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const autoLoginAttempted = useRef(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || !useSupabase) return;

    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const type = params.get("type");

    if (!accessToken || !refreshToken || type !== "recovery") return;

    autoLoginAttempted.current = true;
    setRecoveryProcessing(true);

    (async () => {
      try {
        const { getSupabaseBrowserClient } = await import("@/lib/supabase/client");
        const supabase = getSupabaseBrowserClient();

        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        window.history.replaceState(null, "", window.location.pathname + window.location.search);

        if (error) {
          setError("Recovery link is invalid or has expired. Please request a new one.");
          setRecoveryProcessing(false);
          return;
        }

        router.push("/reset-password");
        router.refresh();
      } catch {
        setError("Could not process recovery link.");
        setRecoveryProcessing(false);
      }
    })();
  }, [useSupabase, router]);

  useEffect(() => {
    if (autoLoginAttempted.current) return;
    if (process.env.NEXT_PUBLIC_PREVIEW_AUTO_LOGIN !== "true") return;
    if (!useSupabase) return;

    autoLoginAttempted.current = true;
    setAutoLoggingIn(true);

    (async () => {
      try {
        const res = await fetch("/api/auth/preview-credentials");
        if (!res.ok) {
          setAutoLoggingIn(false);
          return;
        }
        const creds = await res.json();

        const { getSupabaseBrowserClient } = await import("@/lib/supabase/client");
        const supabase = getSupabaseBrowserClient();
        const { error: authError } = await supabase.auth.signInWithPassword({
          email: creds.email,
          password: creds.password,
        });

        if (authError) {
          setError(`Auto-login failed: ${authError.message}. Sign in manually.`);
          setAutoLoggingIn(false);
        } else {
          finishSignIn();
        }
      } catch {
        setAutoLoggingIn(false);
      }
    })();
  }, [useSupabase, finishSignIn]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes("error")) {
      const params = new URLSearchParams(hash.substring(1));
      const desc = params.get("error_description");
      if (desc) {
        setError(desc.replace(/\+/g, " "));
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    }

    const errorParam = searchParams.get("error");
    if (errorParam) {
      setError(errorParam);
    }
  }, [searchParams]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (useSupabase) {
      try {
        const { getSupabaseBrowserClient } = await import("@/lib/supabase/client");
        const supabase = getSupabaseBrowserClient();

        if (mode === "forgot_password") {
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${location.origin}/auth/callback/recovery`,
          });
          if (error) {
            setError(error.message);
          } else {
            setResetSent(true);
          }
        } else if (mode === "magic_link") {
          const { error } = await supabase.auth.signInWithOtp({
            email,
            options: { emailRedirectTo: `${location.origin}/auth/callback` },
          });
          if (error) {
            setError(error.message);
          } else {
            setMagicSent(true);
          }
        } else {
          const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          if (error) {
            setError(error.message);
          } else {
            finishSignIn();
          }
        }
      } catch {
        setError("Supabase connection failed. Check your configuration.");
      }
    } else {
      try {
        const res = await fetch("/api/auth/dev-login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });

        const data = await res.json();

        if (!res.ok) {
          setError(data.error || "Sign-in failed");
        } else {
          finishSignIn();
        }
      } catch {
        setError("Connection error. Is the dev server running?");
      }
    }

    setLoading(false);
  }

  if (recoveryProcessing) {
    return (
      <AuthShell>
        <CenteredCard>
          <BrandSpinner size={28} className="mx-auto mb-3" />
          <h2 className="font-display text-lg font-semibold mb-1 text-brand-ink">Password Recovery</h2>
          <p className="text-brand-muted text-sm">Processing your reset link…</p>
        </CenteredCard>
      </AuthShell>
    );
  }

  if (autoLoggingIn) {
    return (
      <AuthShell>
        <CenteredCard>
          <BrandSpinner size={28} className="mx-auto mb-3" />
          <h2 className="font-display text-lg font-semibold mb-1 text-brand-ink">Preview Auto-Login</h2>
          <p className="text-brand-muted text-sm">Signing in automatically…</p>
        </CenteredCard>
      </AuthShell>
    );
  }

  if (magicSent) {
    return (
      <AuthShell>
        <CenteredCard>
          <h2 className="font-display text-xl font-semibold mb-2 text-brand-ink">Check your email</h2>
          <p className="text-brand-muted text-sm">
            We sent a sign-in link to <strong className="text-brand-ink">{email}</strong>.
            Click the link in the email to continue.
          </p>
        </CenteredCard>
      </AuthShell>
    );
  }

  if (resetSent) {
    return (
      <AuthShell>
        <CenteredCard>
          <h2 className="font-display text-xl font-semibold mb-2 text-brand-ink">Check your email</h2>
          <p className="text-brand-muted text-sm">
            We sent a password reset link to <strong className="text-brand-ink">{email}</strong>. Click the
            link in the email to set a new password.
          </p>
          <button
            type="button"
            onClick={() => { setResetSent(false); setMode("password"); }}
            className="inline-block mt-4 text-sm text-accent-600 hover:text-accent-700 hover:underline"
          >
            Back to sign in
          </button>
        </CenteredCard>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="w-full max-w-sm">
        <div className="mb-7">
          <div className="flex items-center gap-2 mb-4">
            <BrandLogoMark size={32} />
            <span className="font-display text-xl font-bold tracking-tight text-brand-ink">VAON</span>
          </div>
          <h1 className="font-display text-2xl font-semibold text-brand-ink mb-1">
            {mode === "forgot_password" ? "Reset your password" : "Welcome back"}
          </h1>
          <p className="text-sm text-brand-muted">
            {mode === "forgot_password"
              ? "Enter your email and we'll send a reset link."
              : "Sign in to continue documenting your electrical systems."}
          </p>
        </div>

        {!useSupabase && (
          <div className="mb-4 bg-accent-50 border border-accent-200 rounded-md px-3 py-2 text-xs text-accent-700">
            <strong>Local dev mode</strong> — Supabase is not configured.
            Sign in with your dev credentials from <code>.env.local</code>.
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-ink mb-1">
              Email
            </label>
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-brand-line rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:border-accent-500"
              placeholder="you@company.com"
            />
          </div>

          {mode === "password" && (
            <div>
              <label className="block text-sm font-medium text-brand-ink mb-1">
                Password
              </label>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-brand-line rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:border-accent-500"
              />
            </div>
          )}

          {mode === "password" && useSupabase && (
            <div className="text-right -mt-2">
              <button
                type="button"
                onClick={() => { setMode("forgot_password"); setError(null); }}
                className="text-sm text-accent-600 hover:text-accent-700 hover:underline"
              >
                Forgot password?
              </button>
            </div>
          )}

          {error && (
            <p className="text-semantic-danger text-sm bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-accent-500 text-white py-2.5 px-4 rounded-md text-sm font-medium hover:bg-accent-400 active:bg-accent-600 disabled:opacity-50 transition-colors shadow-sm"
          >
            {loading
              ? mode === "forgot_password"
                ? "Sending…"
                : "Signing in…"
              : mode === "forgot_password"
              ? "Send reset link"
              : mode === "password"
              ? "Sign in"
              : "Send magic link"}
          </button>
        </form>

        {useSupabase && mode !== "forgot_password" && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() =>
                setMode(mode === "password" ? "magic_link" : "password")
              }
              className="text-sm text-accent-600 hover:text-accent-700 hover:underline"
            >
              {mode === "password"
                ? "Sign in with a magic link instead"
                : "Sign in with password instead"}
            </button>
          </div>
        )}

        {mode === "forgot_password" ? (
          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => { setMode("password"); setError(null); }}
              className="text-sm text-brand-muted hover:underline"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <div className="mt-3 text-center">
            <a href="/sign-up" className="text-sm text-brand-muted hover:text-brand-ink hover:underline">
              No account? <span className="text-accent-600">Sign up</span>
            </a>
          </div>
        )}
      </div>
    </AuthShell>
  );
}

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex bg-brand-paper">
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        {children}
      </div>
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-brand-ink">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 30% 20%, rgba(255, 160, 74, 0.35), transparent 55%), radial-gradient(ellipse at 70% 80%, rgba(255, 78, 46, 0.30), transparent 60%), #0B1220",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #FFFFFF 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-2">
            <BrandLogoMark size={28} variant="accent" />
            <span className="font-display text-lg font-bold tracking-tight">VAON</span>
          </div>
          <div>
            <h2 className="font-display text-4xl font-semibold leading-tight mb-4 max-w-md">
              Electrical documentation,{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(90deg, #FFA04A 0%, #FF4E2E 100%)" }}
              >
                rewired.
              </span>
            </h2>
            <p className="text-white/70 text-base max-w-md leading-relaxed">
              Single-line diagrams, floor plan layouts, and Revit sync — all in one
              professional workspace built for electrical engineers.
            </p>
          </div>
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} Konductiv. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white p-8 rounded-xl shadow-sm border border-brand-line max-w-sm w-full text-center">
      {children}
    </div>
  );
}
